import { projectionMatches, type IMenuGroupDaysAvailable } from "@order-up/models";
import { z } from "zod";
import ItemReferenceSchema from "./ItemReference";
import { ModifierGroupReferenceBaseSchema } from "./ModifierGroupReference";
import SubgroupReferenceSchema from "./SubgroupReference";

// Sub-schema for availability
const MenuGroupAvailabilitySchema = z
  .object({
    dineIn: z.boolean().default(true).readonly(),
    takeout: z.boolean().default(true).readonly(),
    delivery: z.boolean().default(true).readonly(),
    kiosk: z.boolean().default(true).readonly(),
    online: z.boolean().default(true).readonly(),
    partners: z.boolean().default(true).readonly(),
  })
  .strict();

// Sub-schema for dateRange (date-only; mirrors Seasonal Availability with isActive toggle)
const DateRangeSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    startDate: z.date().nullable().optional().readonly(),
    endDate: z.date().nullable().optional().readonly(),
    displayMessage: z.string().nullable().optional().readonly(),
  })
  .strict();

// Sub-schema for timeRange (assuming HH:MM format for strings)
const TimeRangeSchema = z
  .object({
    startTime: z
      .string()
      .regex(
        /^([01]\d|2[0-3]):([0-5]\d)$/,
        "Invalid time format, expected HH:MM",
      )
      .nullable()
      .optional()
      .readonly(),
    endTime: z
      .string()
      .regex(
        /^([01]\d|2[0-3]):([0-5]\d)$/,
        "Invalid time format, expected HH:MM",
      )
      .nullable()
      .optional()
      .readonly(),
  })
  .strict()
  .nullable()
  .optional();

// Sub-schema for daysOfWeek / daysAvailable (includes optional timeWindows for per-day windows)
const TimeWindowEntrySchema = z
  .object({
    startTime: z.string(),
    endTime: z.string(),
    isActive: z.boolean(),
  })
  .readonly();

const DaysOfWeekSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    sunday: z.boolean().default(false).readonly(),
    monday: z.boolean().default(false).readonly(),
    tuesday: z.boolean().default(false).readonly(),
    wednesday: z.boolean().default(false).readonly(),
    thursday: z.boolean().default(false).readonly(),
    friday: z.boolean().default(false).readonly(),
    saturday: z.boolean().default(false).readonly(),
    timeWindows: z
      .record(z.string(), TimeWindowEntrySchema)
      .default({})
      .readonly(),
  })
  .strict()
  .default({
    isActive: false,
    sunday: true,
    monday: true,
    tuesday: true,
    wednesday: true,
    thursday: true,
    friday: true,
    saturday: true,
    timeWindows: {},
  });

const timeHHMMSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format, expected HH:MM");

/** Parse "HH:MM" to minutes since midnight (0–1439). Returns null if invalid. */
function parseHHMMToMinutes(s: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(s);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  return h * 60 + m;
}

/** Segments for one window (same-day or overnight). Each [a,b] is minutes, inclusive start. */
function getWindowSegments(startMin: number, endMin: number): [number, number][] {
  if (startMin <= endMin) return [[startMin, endMin]];
  return [
    [startMin, 24 * 60 - 1],
    [0, endMin],
  ];
}

/** True if two segments [a1,a2] and [b1,b2] overlap (share any minute). Touching at a point does not overlap. */
function segmentsOverlap(
  [a1, a2]: [number, number],
  [b1, b2]: [number, number],
): boolean {
  return a1 < b2 && b1 < a2;
}

type TimeWindowLike = { startTime: string; endTime: string; isActive?: boolean };

/** A window is "active" (included in validation and availability) only when both start and end times are entered and valid. */
function activeWindowsOnly(windows: ReadonlyArray<TimeWindowLike>): TimeWindowLike[] {
  return windows.filter(
    (w) =>
      w.isActive !== false &&
      typeof w.startTime === "string" &&
      typeof w.endTime === "string" &&
      w.startTime.trim() !== "" &&
      w.endTime.trim() !== "" &&
      parseHHMMToMinutes(w.startTime) != null &&
      parseHHMMToMinutes(w.endTime) != null,
  );
}

/** True if any pair of time windows overlaps. Windows must have start !== end. Only active windows are considered. */
function doTimeWindowsOverlap(windows: ReadonlyArray<TimeWindowLike>): boolean {
  const active = activeWindowsOnly([...windows]);
  const parsed = active.map((w) => ({
    start: parseHHMMToMinutes(w.startTime),
    end: parseHHMMToMinutes(w.endTime),
  }));
  for (let i = 0; i < parsed.length; i++) {
    const a = parsed[i];
    if (a.start == null || a.end == null || a.start === a.end) continue;
    const segsA = getWindowSegments(a.start, a.end);
    for (let j = i + 1; j < parsed.length; j++) {
      const b = parsed[j];
      if (b.start == null || b.end == null || b.start === b.end) continue;
      const segsB = getWindowSegments(b.start, b.end);
      for (const segA of segsA) {
        for (const segB of segsB) {
          if (segmentsOverlap(segA, segB)) return true;
        }
      }
    }
  }
  return false;
}

/** True if any active window has startTime === endTime. */
function hasEmptyTimeWindow(windows: ReadonlyArray<TimeWindowLike>): boolean {
  return activeWindowsOnly([...windows]).some((w) => w.startTime === w.endTime);
}

/**
 * Validates days-available time windows for real-time or submit validation.
 * Accepts a record (map) keyed by string id. Only active windows are validated for overlap and empty.
 */
export function validateDaysAvailableTimeWindows(
  windows: ReadonlyArray<TimeWindowLike> | Readonly<Record<string, TimeWindowLike>>,
): string | null {
  const list = Array.isArray(windows)
    ? [...windows]
    : Object.values(windows);
  const active = activeWindowsOnly(list);
  if (active.length === 0) return null;
  if (hasEmptyTimeWindow(list)) {
    return "Start and end time cannot be the same in any time window";
  }
  if (doTimeWindowsOverlap(list)) {
    return "Time windows cannot overlap (e.g. 11:00–14:00 and 11:00–16:00 are invalid)";
  }
  return null;
}

const TimeWindowSchema = z
  .object({
    startTime: timeHHMMSchema.readonly(),
    endTime: timeHHMMSchema.readonly(),
    isActive: z.boolean().default(true).readonly(),
  })
  .strict();

/**
 * One day's authored schedule. An EMPTY `windows` array means available ALL DAY —
 * the same meaning an empty window collection has always had.
 */
const DayScheduleSchema = z
  .object({
    isAvailable: z.boolean().readonly(),
    windows: z.array(TimeWindowSchema).readonly(),
  })
  .strict();

/**
 * TOTAL — all seven day keys are required. A partial map is a hard reject rather
 * than a silent fill, because a filled default would invent availability nobody
 * authored. `.strict()` so an unknown day key ("funday") fails loudly instead of
 * being stripped into a schedule that silently loses a day.
 */
const PerDaySchema = z
  .object({
    sunday: DayScheduleSchema,
    monday: DayScheduleSchema,
    tuesday: DayScheduleSchema,
    wednesday: DayScheduleSchema,
    thursday: DayScheduleSchema,
    friday: DayScheduleSchema,
    saturday: DayScheduleSchema,
  })
  .strict();

/**
 * Per-day validation. Overlap is checked WITHIN a day only — the global rule
 * below is day-blind and would refuse the very schedules this field exists to
 * allow (Mon–Fri 18:00–22:00 and Sat 00:00–23:59 overlap on the minute axis).
 */
export function validatePerDaySchedule(
  perDay: Readonly<Record<string, { isAvailable: boolean; windows: ReadonlyArray<TimeWindowLike> }>> | undefined,
): string | null {
  if (perDay == null) return null;
  let anyAvailable = false;
  for (const [dayKey, day] of Object.entries(perDay)) {
    if (day == null) continue;
    if (day.isAvailable) anyAvailable = true;
    const message = validateDaysAvailableTimeWindows(day.windows ?? []);
    if (message != null) {
      const label = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
      return `${label}: ${message}`;
    }
  }
  if (!anyAvailable) {
    return "At least one day must be available";
  }
  return null;
}

// Sub-schema for daysAvailable (mirrors Date/Seasonal pattern with isActive; mutually exclusive with them)
const DaysAvailableSchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    sunday: z.boolean().default(true).readonly(),
    monday: z.boolean().default(true).readonly(),
    tuesday: z.boolean().default(true).readonly(),
    wednesday: z.boolean().default(true).readonly(),
    thursday: z.boolean().default(true).readonly(),
    friday: z.boolean().default(true).readonly(),
    saturday: z.boolean().default(true).readonly(),
    timeWindows: z.record(z.string(), TimeWindowSchema).default({}).readonly(),
    /**
     * Authored per-day schedule. `.optional()` with NO `.default()` — deliberate:
     * `MenuGroupModal` writes the PARSED output, so a default would stamp
     * `perDay` onto every existing group the first time it is saved.
     */
    perDay: PerDaySchema.optional().readonly(),
    startTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format, expected HH:MM")
      .nullable()
      .optional()
      .readonly(),
    endTime: z
      .string()
      .regex(/^([01]\d|2[0-3]):([0-5]\d)$/, "Invalid time format, expected HH:MM")
      .nullable()
      .optional()
      .readonly(),
  })
  .strict();

// Sub-schema for seasonalAvailability
const SeasonalAvailabilitySchema = z
  .object({
    isActive: z.boolean().default(false).readonly(),
    startMonth: z
      .number()
      .int()
      .min(1)
      .max(12)
      .nullable()
      .optional()
      .readonly(),
    startDay: z.number().int().min(1).max(31).nullable().optional().readonly(),
    endMonth: z.number().int().min(1).max(12).nullable().optional().readonly(),
    endDay: z.number().int().min(1).max(31).nullable().optional().readonly(),
    displayMessage: z.string().nullable().optional().readonly(),
  })
  .strict();

// Base object schema without refinements (for extend/omit operations)
export const MenuGroupBaseSchema = z
  .object({
    menuGroupId: z.number().nullable().optional().readonly(),
    // TODO: Reinstate .uuid() validation once mock data or server-side generation provides valid UUIDs.
    guid: z.string().nullable().optional().readonly(),
    number: z.number().nullable().optional().readonly(),
    // For nested groups: Supports multiple parents
    parentIds: z.array(z.number().int()).default([]).readonly(),
    // Deprecated: parentId preserved for backward compatibility during migration
    parentId: z.number().int().nullable().optional().readonly(),
    isEditing: z.boolean().default(false).nullable().optional().readonly(), // UI state, often not validated strictly or omitted for persistence
    name: z
      .string()
      .min(1, { message: "Menu group name cannot be empty" })
      .readonly(),
    posName: z.string().nullable().optional().default("").readonly(),
    buttonColor: z.string().nullable().optional().readonly(), // Consider hex validation
    position: z.number().int().default(0).readonly(),
    description: z.string().nullable().optional().default("").readonly(),
    items: z.array(ItemReferenceSchema).default([]).readonly(),
    modifierGroups: z
      .array(ModifierGroupReferenceBaseSchema)
      .default([])
      .readonly(),
    /** Optimistic revision of `modifierGroups` — see `IMenuGroup.modifierGroupsRev`. */
    modifierGroupsRev: z.number().int().min(0).optional().readonly(),
    // Accept URL strings, JSON stringified OptimizedImage objects, or null/undefined
    // Legacy: URL string, Modern: JSON stringified OptimizedImage object
    image: z
      .string()
      .refine(
        (val) => {
          if (!val) return true; // Allow empty strings
          // Check if it's a valid URL
          try {
            new URL(val);
            return true;
          } catch {
            // Not a URL, check if it's valid JSON (OptimizedImage object)
            try {
              const parsed = JSON.parse(val);
              // Basic validation that it looks like an OptimizedImage
              return (
                parsed && typeof parsed === "object" && "original" in parsed
              );
            } catch {
              return false;
            }
          }
        },
        { message: "Image must be a valid URL or OptimizedImage JSON" },
      )
      .nullable()
      .optional()
      .readonly(),

    isMenu: z.boolean().default(false).readonly(),
    isActive: z.boolean().default(true).readonly(),
    isArchived: z.boolean().default(false).readonly(),

    pricingType: z
      .enum(["standard", "fixed", "discount", "special"])
      .default("standard")
      .readonly(),
    fixedPrice: z.number().min(0).nullable().optional().readonly(),
    discountPercentage: z
      .number()
      .min(0)
      .max(100)
      .nullable()
      .optional()
      .readonly(), // Percentage
    minRequired: z.number().int().min(0).nullable().optional().readonly(),
    maxSelections: z.number().int().min(0).nullable().optional().readonly(),

    // ── Group-level pricing (new system — does NOT touch legacy pricingType/fixedPrice/discountPercentage) ──
    groupPricingStrategy: z
      .enum(["off", "fixed", "size_based", "menu_specific"])
      .default("off")
      .readonly(),
    groupFixedPrice: z.number().min(0).nullable().optional().readonly(),
    groupSizePrices: z
      .array(
        z
          .object({
            name: z.string().min(1, "Size name is required").readonly(),
            price: z.number().min(0, "Price must be >= 0").readonly(),
          })
          .strict(),
      )
      .default([])
      .readonly(),

    visibleInPOS: z.boolean().default(true).readonly(),
    visibleInKiosk: z.boolean().default(true).readonly(),
    visibleInOnlineOrdering: z.boolean().default(true).readonly(),
    visibleToPartners: z.boolean().default(true).readonly(),
    visibleInInvoices: z.boolean().default(true).readonly(),

    inheritHideModifierPrices: z.boolean().default(true).readonly(),

    availability: MenuGroupAvailabilitySchema.default({
      // Apply default for the whole object
      dineIn: true,
      takeout: true,
      delivery: true,
      kiosk: true,
      online: true,
      partners: true,
    }).readonly(),

    dateRange: DateRangeSchema.default({ isActive: false }).readonly(),
    timeRange: TimeRangeSchema.readonly(), // Already optional
    daysOfWeek: DaysOfWeekSchema.readonly(),
    daysAvailable: DaysAvailableSchema.default({
      isActive: false,
      sunday: true,
      monday: true,
      tuesday: true,
      wednesday: true,
      thursday: true,
      friday: true,
      saturday: true,
      timeWindows: {},
    }).readonly(),

    seasonalAvailability: SeasonalAvailabilitySchema.readonly(),

    tagIds: z
      .array(z.number().int())
      .nullable()
      .optional()
      .default([])
      .readonly(),

    // Kitchen / Preparation — prep stations assigned at the group level
    prepStationIds: z
      .array(z.number().int())
      .nullable()
      .optional()
      .default([])
      .readonly(),

    // Course assignment at the group level — overrides item-level courseId
    courseId: z.number().int().positive().nullable().optional().readonly(),

    // Dynamic prep time — minutes inherited by the group's items (nearest group
    // wins). See docs/DYNAMIC_PREP_TIME_ARCHITECTURE.md. `null` clears.
    prepTimeMinutes: z.number().min(0).max(240).nullable().optional().readonly(),

    // Job title IDs assigned at the group level
    jobTitleIds: z
      .array(z.number().int())
      .nullable()
      .optional()
      .default([])
      .readonly(),

    isSpecial: z.boolean().nullable().optional().default(false).readonly(),
    isPriority: z.boolean().nullable().optional().default(false).readonly(),

    // Per-parent overrides for child sub-groups (pricing, visibility, scheduling)
    // Uses full schema with cross-field refinements (fixed requires price, size_based requires sizes)
    subgroups: z.array(SubgroupReferenceSchema).default([]).readonly(),

    // Edit mode tracking fields (used to track deletions during update operations)
    removedSubgroupIds: z
      .array(z.number().int())
      .optional()
      .default([])
      .readonly(),
    removedItemIds: z.array(z.number().int()).optional().default([]).readonly(),
    removedModifierGroupIds: z
      .array(z.number().int())
      .optional()
      .default([])
      .readonly(),

    archivedAt: z.date().nullable().optional().readonly(),
    createdAt: z.date().nullable().optional().readonly(), // Firestore server timestamp
    updatedAt: z.date().nullable().optional().readonly(), // Firestore server timestamp
  })
  .strict();

// Schema with refinements for validation (built from base schema)
const MenuGroupSchema = MenuGroupBaseSchema.refine(
  (data) => {
    // Example refinement for pricing types
    if (data.pricingType === "fixed") {
      return data.fixedPrice !== undefined && data.fixedPrice !== null;
    }
    if (data.pricingType === "discount") {
      return (
        data.discountPercentage !== undefined &&
        data.discountPercentage !== null
      );
    }
    return true;
  },
  { message: "Price/discount details do not match pricing type" },
).refine(
  (data) =>
    !(
      data.maxSelections !== undefined &&
      data.maxSelections !== null &&
      data.minRequired !== undefined &&
      data.minRequired !== null &&
      data.maxSelections < data.minRequired
    ),
  {
    message:
      "Maximum selections cannot be less than minimum required selections",
    path: ["maxSelections"],
  },
)
.refine(
  (data) =>
    !hasEmptyTimeWindow(
      Object.values((data.daysAvailable?.timeWindows ?? {}) as Record<string, TimeWindowLike>),
    ),
  {
    message: "Start and end time cannot be the same in any time window",
    path: ["daysAvailable", "timeWindows"],
  },
)
.refine(
  (data) =>
    !doTimeWindowsOverlap(
      Object.values((data.daysAvailable?.timeWindows ?? {}) as Record<string, TimeWindowLike>),
    ),
  {
    message:
      "Time windows cannot overlap (e.g. 11:00–14:00 and 11:00–16:00 are invalid)",
    path: ["daysAvailable", "timeWindows"],
  },
)
// ── Per-day scheduling refinements ──
//
// EVERY ONE of these no-ops when `perDay` is absent. Without that, every
// existing production document fails to save — measured: removing it turns 26
// tests red, including the plain "all seven days, no windows" shape that most
// production groups have.
//
// The guard is deliberately in TWO places: the `perDay == null ||` below and
// the same early return inside `projectionMatches` itself. Either alone is
// sufficient, so the negative control has to remove BOTH to observe red — one
// on its own stays green and proves nothing.
//
// The two global refinements above stay untouched and only ever see the
// projection, which is a single uniform list and therefore always valid.
.superRefine((data, ctx) => {
  const message = validatePerDaySchedule(
    data.daysAvailable?.perDay as
      | Record<string, { isAvailable: boolean; windows: TimeWindowLike[] }>
      | undefined,
  );
  if (message != null) {
    ctx.addIssue({
      code: "custom",
      message,
      path: ["daysAvailable", "perDay"],
    });
  }
})
.refine(
  // The stored day booleans and timeWindows MUST equal the projection of the
  // stored perDay. A caller cannot persist authored data beside a stale
  // projection — divergence has to be unrepresentable, not merely discouraged.
  (data) =>
    data.daysAvailable?.perDay == null ||
    projectionMatches(data.daysAvailable as unknown as IMenuGroupDaysAvailable),
  {
    message:
      "Day availability and time windows must match the per-day schedule. Build them with buildDaysAvailableForWrite().",
    path: ["daysAvailable", "timeWindows"],
  },
)
// ── Group-level pricing refinements ──
.refine(
  (data) => {
    if (data.groupPricingStrategy === "fixed") {
      return data.groupFixedPrice !== undefined && data.groupFixedPrice !== null && data.groupFixedPrice >= 0;
    }
    return true;
  },
  {
    message: "Group fixed price is required when group pricing strategy is 'fixed'",
    path: ["groupFixedPrice"],
  },
)
.refine(
  (data) => {
    if (data.groupPricingStrategy === "size_based") {
      return data.groupSizePrices && data.groupSizePrices.length > 0;
    }
    return true;
  },
  {
    message: "At least one size tier is required for size-based group pricing",
    path: ["groupSizePrices"],
  },
)
.refine(
  (data) => {
    if (data.groupPricingStrategy === "size_based" && data.groupSizePrices && data.groupSizePrices.length > 1) {
      const names = data.groupSizePrices.map((s) => s.name.toLowerCase().trim());
      return new Set(names).size === names.length;
    }
    return true;
  },
  {
    message: "Size tier names must be unique",
    path: ["groupSizePrices"],
  },
);

export type MenuGroupType = z.infer<typeof MenuGroupSchema>;

export default MenuGroupSchema;
